/* rb-b-misc.js — fold the Misc bucket into the real sizes and remove it.

   CUSTOMER-SIDE ONLY. Nothing in Shopify changes; this hides rows and drives
   the theme's own checkboxes, like rb-b-sizeclean.js and rb-b-shoes.js.

   WHAT WAS THERE. Misc held three visible values on /collections/all, three
   products each:

     Large/ X- Large (3)    Small/ Medium (3)    16x20 (3)

   Nine products behind a whole section, and — worse — invisible to the way
   people actually shop. Someone filtering Medium never saw the "Small/ Medium"
   items, because that is a separate filter value.

   WHAT IT DOES NOW. A combo size folds into BOTH of its halves: picking Medium
   applies Medium AND Small/ Medium; picking Large applies Large AND
   Large/ X- Large. That is honest — a garment sized S/M genuinely fits both —
   and it makes those products findable instead of stranded. With the combos
   folded and 16x20 hidden, nothing is left under Misc, so the heading goes too.

   16x20 IS NOT A SIZE. It is a print dimension that landed on the size axis.
   Hidden here, not fixed in the catalog — Mark's call, and this file cannot
   fix data anyway. Only values shaped like WxH are treated this way; an
   unrecognised Misc value is LEFT ALONE and keeps its heading, because
   silently hiding a filter nobody has looked at is how things go missing.

   TWO COLLISIONS, both learned the hard way on rb-b-shoes.js:

   1. sizeclean has usually already bound a click handler to the traditional
      row (Small absorbed S, X- Large absorbed XL). A second listener on the
      same element toggles the shared inputs twice and cancels out, and
      capture does not help because listeners on the SAME element fire in
      registration order. So the link is replaced with a clone, which drops
      its listeners, and this module rebinds with the full input set —
      original + whatever sizeclean merged + the combo.

   2. Counts must be idempotent. This runs again after every swap, and adding
      the combo's count to a total it already added produces 289, 292, 295...
      The original is captured once in data-rbb-misc-orig and every later pass
      recomputes from that.

   THE PILL. Applying Medium now sends two values, so the theme renders a
   "Small/ Medium" pill next to "Medium". That companion pill is hidden — but
   only while one of its parent sizes is also active, so it can never become
   the only pill and leave the shopper unable to clear it.

   Loads AFTER sizeclean and shoes; re-runs on a real swap only (rbbSwap). */

(function () {
  'use strict';

  /* RUNS ON MOBILE TOO, since 10 Sep. The 981px gate that used to be here was
     added under "mobile should be exactly as it is on the live site", which
     was about the desktop PANEL layout. This module injects no markup and
     needs no CSS — it hides rows and drives the theme's own checkboxes — and
     the mobile drawer uses identical markup, so it is portable as-is. Mark
     approved after seeing the before/after. rb-b-pants.js stays gated: its
     waist/inseam control is built markup and needs styling for mobile first. */

  var DIMENSION = /^\d{1,3}\s*[xX]\s*\d{1,3}$/;     // 16x20 — a print, not a size

  var CANON = {
    small: 's', medium: 'm', large: 'l',
    xsmall: 'xs', xlarge: 'xl', xxlarge: 'xxl', xxxlarge: 'xxxl'
  };

  /* Same normalisation sizeclean uses, so the keys line up. */
  function norm(v) {
    var s = (v || '').trim().toLowerCase();
    s = s.replace(/[\s\-_]+/g, '');
    s = s.replace(/extra/g, 'x');
    if (/^0\d+$/.test(s)) s = s.replace(/^0+/, '') || '0';
    return CANON[s] || s;
  }

  function isSubhead(el) {
    return !!(el && el.classList &&
      el.classList.contains('collection-sidebar__size-subheader'));
  }

  function rowLabel(row) {
    var el = row.querySelector('.collection-sidebar__link');
    if (!el) return '';
    var clone = el.cloneNode(true);
    var c = clone.querySelector('.collection-sidebar__link__count');
    if (c) c.remove();
    return clone.textContent.trim().replace(/\s+/g, ' ');
  }

  function readCount(row) {
    var c = row.querySelector('.collection-sidebar__link__count');
    var n = c ? parseInt((c.textContent || '').replace(/[^\d]/g, ''), 10) : NaN;
    return isNaN(n) ? 0 : n;
  }

  function origCount(row) {
    if (!row.dataset.rbbMiscOrig) row.dataset.rbbMiscOrig = String(readCount(row));
    return parseInt(row.dataset.rbbMiscOrig, 10) || 0;
  }

  function apply() {
    var grp = document.querySelector('.collection-sidebar__filter-group--size');
    if (!grp) return;
    var ul = grp.querySelector('.collection-sidebar__items');
    if (!ul) return;

    var kids = Array.prototype.slice.call(ul.children);

    /* Walk the whole list once: every bucket's rows keyed by normalised
       label, and the Misc bucket collected separately. */
    var byKey = {};
    var misc = [];
    var traditional = [];
    var miscHead = null;
    var bucket = null;

    kids.forEach(function (el) {
      if (isSubhead(el)) {
        var t = (el.textContent || '').trim().toLowerCase();
        bucket = t.indexOf('misc') !== -1 ? 'misc'
               : (t.indexOf('traditional') !== -1 ? 'traditional' : 'other');
        if (bucket === 'misc') miscHead = el;
        return;
      }
      if (!el.classList || !el.classList.contains('collection-sidebar__item')) return;

      var input = el.querySelector('input');
      if (!input) return;
      var label = rowLabel(el);

      if (bucket === 'misc') {
        if (el.dataset.rbbHidden === 'junk') return;   // sizeclean already dealt with it
        misc.push({ row: el, input: input, label: label, count: origCount(el) });
        return;
      }

      var key = norm(label);
      (byKey[key] = byKey[key] || []).push(input);
      if (bucket === 'traditional' && !el.dataset.rbbHidden) {
        traditional.push({ row: el, key: key, input: input });
      }
    });

    if (!misc.length) return;

    /* Fold each combo into its halves. */
    var extra = {};      // traditional key -> [inputs from Misc]
    var addCount = {};   // traditional key -> products to add to the count
    var folded = [];     // combo labels we absorbed, for the pill cleanup

    misc.forEach(function (m) {
      if (m.row.dataset.rbbMisc) return;              // already handled

      if (DIMENSION.test(m.label)) {                  // 16x20 and friends
        m.row.style.setProperty('display', 'none', 'important');
        m.row.dataset.rbbMisc = 'dimension';
        m.row.dataset.rbbHidden = m.row.dataset.rbbHidden || 'misc';
        return;
      }

      if (m.label.indexOf('/') === -1) return;        // not a combo: leave it alone

      var parts = m.label.split('/')
        .map(function (s) { return norm(s); })
        .filter(Boolean);

      /* Every half must match a size the list actually shows. That single
         test is the whole guard: it is what stops this inventing a fold for
         something like "Left/ Right" or a half whose size is not in this
         collection, in which case the row is left visible and filterable. */
      var usable = parts.filter(function (k) { return byKey[k]; });
      if (!parts.length || usable.length !== parts.length) return;

      usable.forEach(function (k) {
        (extra[k] = extra[k] || []).push(m.input);
        addCount[k] = (addCount[k] || 0) + m.count;
      });

      m.row.style.setProperty('display', 'none', 'important');
      m.row.dataset.rbbMisc = 'folded';
      m.row.dataset.rbbHidden = m.row.dataset.rbbHidden || 'misc';
      folded.push(m.label);
    });

    /* Rebind the traditional rows that gained values. */
    traditional.forEach(function (t) {
      if (!extra[t.key] || !extra[t.key].length) return;

      var inputs = (byKey[t.key] || []).concat(extra[t.key]);
      var total = origCount(t.row) + (addCount[t.key] || 0);

      var countEl = t.row.querySelector('.collection-sidebar__link__count');
      if (countEl) countEl.textContent = total;

      var link = t.row.querySelector('.collection-sidebar__link');
      if (!link) return;

      /* sizeclean may own a handler here — see note 1. Cloning drops it. */
      if (link.dataset.rbbMiscBound !== '1') {
        var fresh = link.cloneNode(true);
        delete fresh.dataset.rbbMerged;
        fresh.dataset.rbbMiscBound = '1';
        link.parentNode.replaceChild(fresh, link);
        link = fresh;

        link.addEventListener('click', function (ev) {
          ev.preventDefault();
          var turnOn = !inputs.some(function (i) { return i.checked; });
          inputs.forEach(function (i) { i.checked = turnOn; });
          if (inputs[0]) inputs[0].dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });

    dropEmptyMiscHead(ul, miscHead);
    hideCompanionPills(folded);
  }

  function dropEmptyMiscHead(ul, head) {
    if (!head) return;
    var kids = Array.prototype.slice.call(ul.children);
    var i = kids.indexOf(head);
    if (i === -1) return;
    var visible = 0;
    for (var j = i + 1; j < kids.length; j++) {
      if (isSubhead(kids[j])) break;
      if (getComputedStyle(kids[j]).display !== 'none') visible++;
    }
    head.style.display = visible ? '' : 'none';
  }

  /* A folded combo rides along with its parent size, so its pill is noise —
     but only hide it while a parent pill is actually showing, or it could
     become the only pill and leave nothing to click. */
  function hideCompanionPills(folded) {
    if (!folded.length) return;
    var pills = Array.prototype.slice.call(document.querySelectorAll(
      '.collection__filters-active__filter:not(.collection__filters-active__filter--clear)'));
    if (pills.length < 2) return;

    var texts = pills.map(function (p) { return (p.textContent || '').trim().replace(/\s+/g, ' '); });

    pills.forEach(function (pill, idx) {
      var t = texts[idx];
      if (folded.indexOf(t) === -1) return;
      var parents = t.split('/').map(function (s) { return norm(s); });
      var parentShowing = texts.some(function (other, j) {
        return j !== idx && parents.indexOf(norm(other)) !== -1;
      });
      if (parentShowing) pill.style.setProperty('display', 'none', 'important');
    });
  }

  function boot() {
    apply();
    var section = document.querySelector('.js-section__collection');
    if (!section || !window.MutationObserver) return;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (window.rbbSwap && !window.rbbSwap.changed('misc')) return;
        apply();
      }, 160);            // after sizeclean (90) and shoes (140)
    }).observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
